import { ref, watch, type Ref } from "vue"

/*

Composable for managing favorite ("bookmarked") Raumplanung indicators.

Favorites are persisted in localStorage as a JSON array of attribute names
(andromedaAttrName strings). The label for a favorite is resolved at render
time from the /toc_tree response (see RequestOptionsAttributeSelector.vue),
so this composable only needs to store the attribute names.

*/

const STORAGE_KEY = "atlas-raumplanung-favorite-attributes"

function loadFavoritesFromStorage(): string[] {

    const str = localStorage.getItem(STORAGE_KEY)

    if (str == null) {
        return []
    }

    try {
        const parsed = JSON.parse(str)
        if (!Array.isArray(parsed)) {
            return []
        }
        // Filter to plain strings only
        return parsed.filter((entry): entry is string => typeof entry === "string")
    }
    catch (e) {
        return []
    }

}

function saveFavoritesToStorage(favorites: string[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites))
    }
    catch (e) {
        // localStorage may be unavailable (quota exceeded, private mode, etc.) —
        // favorites then just live in-memory for this session.
        console.warn("Could not persist favorite attributes to localStorage", e)
    }
}

// Module-level singleton so that all components using this composable share
// the same favorites state.
const favorites: Ref<string[]> = ref(loadFavoritesFromStorage())

// Persist whenever the list changes. deep: true is needed for in-place
// mutations (push/splice).
watch(favorites, (newVal) => {
    saveFavoritesToStorage([...newVal])
}, { deep: true })


export function useFavoriteAttributes() {

    function isFavorite(attrName: string): boolean {
        return favorites.value.includes(attrName)
    }

    function toggleFavorite(attrName: string): void {
        const index = favorites.value.indexOf(attrName)
        if (index === -1) {
            favorites.value.push(attrName)
        }
        else {
            favorites.value.splice(index, 1)
        }
    }

    return {
        favorites,
        isFavorite,
        toggleFavorite,
    }
}
